// lifecycleStageSlice.js — minimal functional-only Lifecycle CRM frontend (Module 7).
// UI polish is a deliberate later pass; this just needs to exercise the already-built
// and already-tested Modules 1-6 end to end.
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Same client-side dedup pattern as userDetailsSlice.js's createCachedThunk —
// re-implemented locally since it isn't exported from that file.
const requestCache = new Map();
const CACHE_DURATION = 10000;

const createCachedThunk = (key, asyncThunk) => {
  return (...args) => (dispatch) => {
    const force = args[0]?.force === true;
    const now = Date.now();
    const cached = requestCache.get(key);
    if (!force && cached && now - cached.timestamp < CACHE_DURATION) {
      return Promise.resolve(cached.result);
    }
    const promise = dispatch(asyncThunk(...args));
    requestCache.set(key, { timestamp: now, result: promise });
    return promise;
  };
};

export const __resetLifecycleStageCache = () => {
  requestCache.clear();
};

const fetchLifecycleStagesRaw = createAsyncThunk(
  "lifecycleStage/fetchStages",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/lifecycle-stages`);
      return res.data.stages;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchLifecycleStages = createCachedThunk("fetchLifecycleStages", fetchLifecycleStagesRaw);

const fetchLifecycleStageCountsRaw = createAsyncThunk(
  "lifecycleStage/fetchCounts",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/lifecycle-stages/counts`);
      return res.data.counts;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchLifecycleStageCounts = createCachedThunk(
  "fetchLifecycleStageCounts",
  fetchLifecycleStageCountsRaw
);

// Shapes the backend's [{stageId,count,unassignedCount}] array into the two
// stageId-keyed maps the reducer/consumers use — shared by both the counts-only
// and stages+counts reducer cases below so the mapping logic isn't duplicated.
const shapeCounts = (countsArray) => ({
  counts: Object.fromEntries((countsArray || []).map((c) => [c.stageId, c.count])),
  unassignedCounts: Object.fromEntries((countsArray || []).map((c) => [c.stageId, c.unassignedCount || 0])),
});

// Every real caller (Inbox rail, Lifecycle Settings, Dashboard pipeline) always
// fetches stages + counts together — this is that in one round trip
// (GET /api/lifecycle-stages?withCounts=true) instead of the two separate
// calls above. Plain fetchLifecycleStages() stays as-is for lean callers that
// only need the stage list (LifecycleStageSelect's dropdown).
const fetchLifecycleStagesWithCountsRaw = createAsyncThunk(
  "lifecycleStage/fetchStagesWithCounts",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/lifecycle-stages`, {
        params: { withCounts: true },
      });
      return { stages: res.data.stages, counts: res.data.counts };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchLifecycleStagesWithCounts = createCachedThunk(
  "fetchLifecycleStagesWithCounts",
  fetchLifecycleStagesWithCountsRaw
);

export const createLifecycleStage = createAsyncThunk(
  "lifecycleStage/createStage",
  async ({ name, color, icon, stageType }, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE_URL}/api/lifecycle-stages`, { name, color, icon, stageType });
      return res.data.stage;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateLifecycleStage = createAsyncThunk(
  "lifecycleStage/updateStage",
  async ({ id, name, color, icon, order, isDefault }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${BASE_URL}/api/lifecycle-stages/${id}`, { name, color, icon, order, isDefault });
      return res.data.stage;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const deleteLifecycleStage = createAsyncThunk(
  "lifecycleStage/deleteStage",
  async ({ id, replacementStageId }, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/api/lifecycle-stages/${id}`, { data: { replacementStageId } });
      return { id };
    } catch (err) {
      return rejectWithValue({
        message: err.response?.data?.message || err.message,
        code: err.response?.data?.code,
        affectedCount: err.response?.data?.affectedCount,
      });
    }
  }
);

// Workspace-level "Lifecycle active" toggle — off just stops new contacts
// auto-assigning to the default stage on first message; everything else
// (manual picking, AI action, this settings page itself) keeps working.
export const fetchLifecycleSettings = createAsyncThunk(
  "lifecycleStage/fetchSettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/lifecycle-stages/settings`);
      return res.data.lifecycleSettings;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateLifecycleSettings = createAsyncThunk(
  "lifecycleStage/updateSettings",
  async ({ active }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${BASE_URL}/api/lifecycle-stages/settings`, { active });
      return res.data.lifecycleSettings;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const moveContactLifecycleStage = createAsyncThunk(
  "lifecycleStage/moveContact",
  async ({ contactId, toStageId }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${BASE_URL}/api/contacts/${contactId}/lifecycle-stage`, { toStageId });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const initialState = {
  stages: [],
  counts: {},
  // stageId -> count of contacts in that stage with Contact.status "Unassigned".
  // Kept as a sibling map (rather than reshaping `counts`) so the existing
  // `counts[stage._id]` consumers (LifecycleSettings.jsx) don't need to change.
  unassignedCounts: {},
  loading: false,
  error: null,
  // Lifecycle-active workspace toggle. Defaults true (matches the schema
  // default) so nothing looks disabled before the settings fetch resolves.
  settingsActive: true,
  settingsLoaded: false,
};

const lifecycleStageSlice = createSlice({
  name: "lifecycleStage",
  initialState,
  reducers: {
    // Bare-signal socket handler calls this after force-refetching — no separate
    // "set stages" action needed since fetchLifecycleStages.fulfilled already covers it.
    resetLifecycleStageError(state) {
      state.error = null;
    },
    // Live per-contact stage move (manual, AI, or future workflow) — patches the
    // two affected stage totals in place from the socket payload instead of a full
    // server refetch, so every open tab in a workspace doesn't re-hit the counts
    // endpoint on every single move. fromStageId is null on a contact's first-ever
    // assignment (nothing to decrement, expected). Deliberately does NOT touch
    // unassignedCounts — that sub-count depends on the contact's conversation
    // status, which isn't in this payload; it stays best-effort/stale until the
    // next real fetch (Dashboard-only, self-corrects on next mount).
    applyLifecycleCountDelta(state, action) {
      const { fromStageId, toStageId } = action.payload || {};
      if (fromStageId && state.counts[fromStageId] > 0) {
        state.counts[fromStageId] -= 1;
      }
      if (toStageId) {
        state.counts[toStageId] = (state.counts[toStageId] || 0) + 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLifecycleStagesRaw.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLifecycleStagesRaw.fulfilled, (state, action) => {
        state.loading = false;
        state.stages = action.payload;
      })
      .addCase(fetchLifecycleStagesRaw.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchLifecycleStageCountsRaw.fulfilled, (state, action) => {
        const shaped = shapeCounts(action.payload);
        state.counts = shaped.counts;
        state.unassignedCounts = shaped.unassignedCounts;
      })
      .addCase(fetchLifecycleStagesWithCountsRaw.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLifecycleStagesWithCountsRaw.fulfilled, (state, action) => {
        state.loading = false;
        state.stages = action.payload.stages;
        const shaped = shapeCounts(action.payload.counts);
        state.counts = shaped.counts;
        state.unassignedCounts = shaped.unassignedCounts;
      })
      .addCase(fetchLifecycleStagesWithCountsRaw.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createLifecycleStage.fulfilled, (state, action) => {
        state.stages.push(action.payload);
      })
      .addCase(updateLifecycleStage.fulfilled, (state, action) => {
        const idx = state.stages.findIndex((s) => s._id === action.payload._id);
        if (idx !== -1) state.stages[idx] = action.payload;
      })
      .addCase(deleteLifecycleStage.fulfilled, (state, action) => {
        state.stages = state.stages.filter((s) => s._id !== action.payload.id);
      })
      .addCase(fetchLifecycleSettings.fulfilled, (state, action) => {
        state.settingsActive = action.payload?.active !== false;
        state.settingsLoaded = true;
      })
      .addCase(updateLifecycleSettings.fulfilled, (state, action) => {
        state.settingsActive = action.payload?.active !== false;
      });
  },
});

export const { resetLifecycleStageError, applyLifecycleCountDelta } = lifecycleStageSlice.actions;
export default lifecycleStageSlice.reducer;
